/**
 * Horizontal icon picker that lets the user choose the chart type for
 * the indicator currently being edited.
 *
 * Includes every legacy chart type so the builder mirrors the
 * EditReportMain.aspx experience: Grid, Bar, Pie, Donut, Line, KPI,
 * Indicator, Summary, Heatmap, Waterfall, Geolocation, MarkUp.
 */

import {
  Table as TableIcon,
  BarChart3,
  PieChart as PieChartIcon,
  CircleDot,
  LineChart as LineChartIcon,
  Gauge,
  Activity,
  LayoutGrid,
  Grid2X2,
  Waves,
  Globe2,
  StickyNote,
  type LucideIcon,
} from 'lucide-react';

export type BuilderChartType =
  | 'grid'
  | 'bar'
  | 'pie'
  | 'donut'
  | 'line'
  | 'kpi'
  | 'indicator'
  | 'summary'
  | 'heatmap'
  | 'waterfall'
  | 'geolocation'
  | 'markup';

interface ChartTypeOption {
  value: BuilderChartType;
  label: string;
  icon: LucideIcon;
}

const OPTIONS: ChartTypeOption[] = [
  { value: 'grid', label: 'Grid', icon: TableIcon },
  { value: 'bar', label: 'Bar', icon: BarChart3 },
  { value: 'pie', label: 'Pie', icon: PieChartIcon },
  { value: 'donut', label: 'Donut', icon: CircleDot },
  { value: 'line', label: 'Line', icon: LineChartIcon },
  { value: 'kpi', label: 'KPI', icon: Gauge },
  { value: 'indicator', label: 'Indicator', icon: Activity },
  { value: 'summary', label: 'Summary', icon: LayoutGrid },
  { value: 'heatmap', label: 'Heatmap', icon: Grid2X2 },
  { value: 'waterfall', label: 'Waterfall', icon: Waves },
  { value: 'geolocation', label: 'Geolocation', icon: Globe2 },
  { value: 'markup', label: 'MarkUp', icon: StickyNote },
];

interface ChartTypePickerProps {
  value: BuilderChartType;
  onChange: (next: BuilderChartType) => void;
}

export function ChartTypePicker({ value, onChange }: ChartTypePickerProps) {
  return (
    <div className="flex flex-wrap gap-2">
      {OPTIONS.map((opt) => {
        const Icon = opt.icon;
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            className={`flex flex-col items-center justify-center gap-1 px-3 py-2 rounded-md border-2 transition-colors min-w-[68px] ${
              active
                ? 'border-primary bg-primary/5 text-primary'
                : 'border-border hover:border-primary/50 text-muted-foreground'
            }`}
            aria-pressed={active}
            aria-label={`Chart type: ${opt.label}`}
          >
            <Icon className="h-4 w-4" />
            <span className="text-[11px] font-medium">{opt.label}</span>
          </button>
        );
      })}
    </div>
  );
}

export const CHART_TYPE_OPTIONS = OPTIONS;
